import {
  Avatar,
  Box,
  Center,
  Flex,
  Grid,
  Portal,
  useOutsideClick,
} from '@chakra-ui/react';
import { SearchItemWrapper } from './SearchItemWrapper';
import { type ContactItem as ContactItemType } from '../types';
import { KTag, KText } from '@/ui-library/components';
import { IconText } from '../IconText';
import { File06, LayersThree02, Mail02, Phone } from '@kula-ai/icons-core';
import { Link } from 'react-router-dom';
import { JobDetailsTable } from './JobDetailsTable';
import { useRef, useState } from 'react';
import { useSearchManager } from '../search-manager';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { HighlightedTooltipText } from '../text';
import { useHistory } from 'react-router-dom';
import { SanitizedText } from '@/ui/SanitizedDom';
import { HTML_TAG_REGEX } from '@/constants/regex';
import { sanitizeName } from '@/utils/sanitizeName';

export const ContactItem = ({ item }: { item: ContactItemType }) => {
  const {
    first_name,
    last_name,
    email,
    image_url,
    position,
    ats_applications = [],
    id,
    name: contactName,
    emails,
    local_phone_number,
    highlights = {},
  } = item;
  const { resume_text = [], sec_emails = [] } = highlights;
  const name = sanitizeName(
    contactName ? contactName : `${first_name || ''} ${last_name || ''}`
  );
  const [showPopover, setShowPopover] = useState(false);
  const wrapperRef = useRef(null);
  const { modalContainerRef, onSearchClose, searchTerm } = useSearchManager();
  const tagRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const history = useHistory();
  const contactEmail =
    (sec_emails.length > 0 &&
      emails?.find((e) =>
        e.email.toLowerCase().includes(searchTerm.toLowerCase())
      )?.email) ||
    emails?.[0]?.email ||
    email ||
    '';

  const resumeText =
    resume_text?.find((text) => {
      const normalizedText = text
        .toLowerCase()
        .trim()
        .replace(HTML_TAG_REGEX, '');
      const normalizedSearchTerm = searchTerm.toLowerCase().trim();
      return normalizedText.includes(normalizedSearchTerm);
    }) || resume_text?.[0];

  useOutsideClick({
    ref: wrapperRef,
    handler: (event) => {
      if (
        popoverRef.current &&
        popoverRef.current.contains(event.target as Node)
      ) {
        return;
      }
      setShowPopover(false);
    },
  });

  const togglePopover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!showPopover && tagRef.current) {
      const tagRect = tagRef.current.getBoundingClientRect();
      const modalRect = modalContainerRef.current.getBoundingClientRect();

      const popoverWidth = 600;
      const gap = 10;

      const top = tagRect.top + tagRect.height / 2 - modalRect.top;
      const left = tagRect.left - popoverWidth - gap - modalRect.left;
      setPopoverPosition({ top, left });
    }
    setShowPopover((prev) => !prev);
    e.stopPropagation();
    e.preventDefault();
  };

  const handleRowClick = (_e: React.MouseEvent) => {
    const selection = window.getSelection();
    if (selection?.toString()) {
      // Text is selected – do NOT navigate
      return;
    }

    // No text is selected – proceed with navigation
    onSearchClose();
    history.push(`/candidates/${id}`);
  };

  return (
    <Box>
      <SearchItemWrapper
        wrapperProps={{
          justifyContent: 'space-between',
          ref: wrapperRef,
          onClick: handleRowClick,
          cursor: 'pointer',
        }}
      >
        <Flex gap="12px" alignItems="center" width="100%" minW={0}>
          <Avatar
            src={image_url}
            name={name}
            borderRadius="xs"
            width="36px"
            height="36px"
            flexShrink={0}
          />
          <Flex flexDirection="column" gap="3px" flex="1" minW={0}>
            <HighlightedTooltipText
              variant="heading_3"
              color="primary-900"
              text={name}
              maxW="100%"
              highlight={searchTerm}
            />
            <Flex
              gap="12px"
              alignItems="center"
              justifyContent="space-between"
              minW={0}
            >
              <Grid
                templateColumns={
                  contactEmail && position ? 'fit-content(50%) auto 1fr' : '1fr'
                }
                alignItems="center"
                gap="6px"
                width="100%"
                minW={0}
              >
                {position && (
                  <Box minW={0}>
                    <HighlightedTooltipText
                      variant="paragraph"
                      color="primary-900"
                      text={position}
                      maxW="100%"
                      highlight={searchTerm}
                    />
                  </Box>
                )}

                {position && contactEmail && (
                  <Box
                    borderRight="1px solid"
                    borderColor="secondary-100"
                    height="100%"
                    my="4px"
                  />
                )}

                {(contactEmail || local_phone_number) && (
                  <Flex minW={0} alignItems="center" gap="6px">
                    {contactEmail && (
                      <Box minW={0} flexShrink={0}>
                        <IconText
                          icon={Mail02}
                          text={contactEmail}
                          textProps={{ maxW: '100%' }}
                          highlight={searchTerm}
                          wrapperProps={{ flexShrink: 0 }}
                        />
                      </Box>
                    )}

                    {contactEmail && local_phone_number && (
                      <Box
                        borderRight="1px solid"
                        borderColor="secondary-100"
                        height="12px"
                        mx="2px"
                      />
                    )}

                    {local_phone_number && (
                      <Box minW={0} flex={1}>
                        <IconText
                          icon={Phone}
                          text={local_phone_number}
                          textProps={{ maxW: '100%' }}
                          highlight={searchTerm}
                        />
                      </Box>
                    )}
                  </Flex>
                )}
              </Grid>
            </Flex>
            {resume_text?.length > 0 && (
              <Flex alignItems="center" gap="6px" minW={0}>
                <Flex
                  gap="6px"
                  border="1px solid"
                  borderColor="border-color"
                  alignItems="center"
                  p="2px 8px"
                  borderRadius="xs"
                  flexShrink={0}
                >
                  <File06 size={16} className="kula-icon" />
                  <KText variant="paragraph">Resume</KText>
                </Flex>
                <SanitizedText
                  html={`...${resumeText}...`}
                  variant="paragraph"
                  color="primary-900"
                  maxW="100%"
                  fontSize="12px"
                />
              </Flex>
            )}
          </Flex>
          {ats_applications?.length > 0 && (
            <Flex gap="6px" alignItems="center" flexShrink={0}>
              <KText
                variant="heading_3"
                color="secondary-700"
                whiteSpace="nowrap"
              >
                Part of
              </KText>
              <Center
                width="24px"
                height="24px"
                borderRadius="xs"
                bg="pastel-7-light"
              >
                <LayersThree02
                  size="1rem"
                  color="var(--kula-colors-pastel-7-dark)"
                  className="kula-icon"
                />
              </Center>
              <Link
                to={`/jobs/${
                  ats_applications[0].ats_job_id || ats_applications[0].job_id
                }?tab=candidates&appId=${ats_applications[0].id}`}
                style={{ textDecoration: 'none' }}
                onClick={(e) => {
                  onSearchClose();
                  e?.stopPropagation();
                }}
              >
                <TruncatedTextWithTooltip
                  className="hover-underline"
                  variant="link"
                  text={ats_applications[0].ats_job_name}
                  maxW={ats_applications.length > 1 ? '240px' : '280px'}
                />
              </Link>
              {ats_applications.length > 1 && (
                <Box ref={tagRef} position="relative">
                  <KTag
                    variant="secondary"
                    size="sm"
                    value={`+${ats_applications.length - 1}`}
                    cursor="pointer"
                    onClick={togglePopover}
                    _hover={{
                      textDecoration: 'underline',
                    }}
                  />
                  {showPopover && (
                    <Portal containerRef={modalContainerRef}>
                      <Box
                        ref={popoverRef}
                        position="absolute"
                        top={`${popoverPosition.top}px`}
                        left={`${popoverPosition.left}px`}
                        transform="translateY(-50%)"
                        width="600px"
                        maxHeight="300px"
                        p="0px"
                        border="1px solid"
                        borderColor="secondary-100"
                        backgroundColor="white"
                        boxShadow="md"
                        overflowY="auto"
                        zIndex={20}
                        borderRadius="md"
                      >
                        <JobDetailsTable ats_applications={ats_applications} />
                      </Box>
                    </Portal>
                  )}
                </Box>
              )}
            </Flex>
          )}
        </Flex>
      </SearchItemWrapper>
    </Box>
  );
};
